/*
 * Copyright 2022. NXGN Management, LLC. All Rights Reserved.
 */

import { Component, Input, OnInit } from "@angular/core";
import { MerchantDetailsParentComponent } from "../merchant-details-parent.component";
import { ActivatedRoute, Router } from "@angular/router";
import { AuthService, MerchantService, NotificationService, StatementService } from "../../../services";
import { Merchant, StatementOptions } from "../../../models";
import { DomSanitizer } from "@angular/platform-browser";
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, ValidatorFn, Validators } from "@angular/forms";
import { PATTERN_CONSTANTS } from "../../../utils/data.rest.constants";

@Component({
  selector: "app-e-statement-options",
  templateUrl: "./e-statement-options.component.html"
})
export class EStatementOptionsComponent extends MerchantDetailsParentComponent implements OnInit {
  logoImage!: any;

  eStatementForm!: FormGroup;
  statementOptions = StatementOptions;
  buttonName = "Submit";
  editable = false;
  actionInProgress = false;
  @Input("merchant") merchant!: Merchant;

  constructor(
    router: Router,
    activatedRoute: ActivatedRoute,
    merchantService: MerchantService,
    notificationService: NotificationService,
    authService: AuthService,
    private statementService: StatementService,
    private sanitizer: DomSanitizer,
    private fb: FormBuilder
  ) {
    super(router, activatedRoute, merchantService, notificationService, authService);
  }

  ngOnInit(): void {
    this.fetchLogo();
    this.createForm();
  }

  createForm() {
    this.eStatementForm = this.fb.group({
      merchantTagLine: [this.merchant.statementOptions[StatementOptions.merchantTagLine], [Validators.maxLength(99)]],
      merchantLogoFilename: [
        {
          value: this.merchant.statementOptions[StatementOptions.merchantLogoFileName],
          disabled: true
        }
      ],
      payOrBillByPhoneNumber: [
        this.merchant.statementOptions[StatementOptions.payByPhoneNo],
        [Validators.pattern(PATTERN_CONSTANTS.NUMBER_10)]
      ],
      payOrBillByPhoneHours: [
        this.merchant.statementOptions[StatementOptions.payByPhoneHours],
        [Validators.maxLength(99)]
      ],
      billQueryNumber: [
        this.merchant.statementOptions[StatementOptions.payQueryPhoneNumber],
        [Validators.pattern(PATTERN_CONSTANTS.NUMBER_10)]
      ],
      billQueryHours: [this.merchant.statementOptions[StatementOptions.billQueryHours], [Validators.maxLength(99)]],
      stmtDueDateLeadDays: [
        this.merchant.statementOptions[StatementOptions.statementDueDateLeadDays],
        [Validators.min(0), Validators.max(60)]
      ],
      statementTestMode: [this.getParsedValue(this.merchant.statementOptions[StatementOptions?.statementTestMode])],
      displayAgingBoxes: [this.getParsedValue(this.merchant.statementOptions[StatementOptions.displayAgingBoxes])],
      displayInsuranceAgingBoxes: [
        {
          value: this.getParsedValue(this.merchant.statementOptions[StatementOptions.displayInsuranceAgingBoxes]),
          disabled: !this.getParsedValue(this.merchant.statementOptions[StatementOptions.displayAgingBoxes])
        }
      ],
      displayStatementDetail: [this.getParsedValue(this.merchant.statementOptions[StatementOptions.displayDetails])],
      payOrBillByCheck: [this.getParsedValue(this.merchant.statementOptions[StatementOptions.payByCheck])],
      payOrBillByMoneyOrder: [this.getParsedValue(this.merchant.statementOptions[StatementOptions.payByMoneyOrder])],
      displayDetachReturnByMail: [
        {
          value: this.getParsedValue(this.merchant.statementOptions[StatementOptions.displayDetachReturnMail]),
          disabled: this.disableReturnByMail()
        },
        this.validateRemitToAddress(this.merchant)
      ],
      displayMerchantName: [this.getParsedValue(this.merchant.statementOptions[StatementOptions.displayMerchantName])]
    });
  }

  fetchLogo() {
    this.statementService.getLogoImage(this.merchant.id).subscribe(
      (logo) => {
        const objectURL = URL.createObjectURL(logo);
        this.logoImage = this.sanitizer.bypassSecurityTrustUrl(objectURL);
        this.editable = false;
        this.actionInProgress = false;
      },
      (error) => this.notificationService.error(error.error)
    );
  }

  getParsedValue(options: string) {
    return options ? JSON.parse(options) : false;
  }

  isStatementDefined(): boolean {
    return !!this.merchant.statementOptions && Object.keys(this.merchant.statementOptions).length !== 0;
  }

  convertStringToBoolean(booleanString: string): boolean {
    return booleanString ? JSON.parse(booleanString) : Boolean(booleanString);
  }

  setEditable(value: boolean) {
    this.editable = value;
  }

  private disableReturnByMail(): boolean {
    return (
      this.merchant.statementOptions[StatementOptions.payByMoneyOrder]?.toLowerCase() === "false" &&
      this.merchant.statementOptions[StatementOptions.payByCheck]?.toLowerCase() === "false"
    );
  }

  saveStatementOptions() {
    if (this.eStatementForm.valid) {
      this.actionInProgress = true;
      const tempStatementOptions = JSON.parse(JSON.stringify(this.merchant.statementOptions));
      tempStatementOptions[StatementOptions.merchantTagLine] = this.eStatementFromControls.merchantTagLine.value;
      tempStatementOptions[StatementOptions.merchantLogoFileName] =
        this.eStatementFromControls.merchantLogoFilename.value;
      tempStatementOptions[StatementOptions.payByPhoneNo] = this.eStatementFromControls.payOrBillByPhoneNumber.value;
      tempStatementOptions[StatementOptions.payByPhoneHours] = this.eStatementFromControls.payOrBillByPhoneHours.value;
      tempStatementOptions[StatementOptions.payQueryPhoneNumber] = this.eStatementFromControls.billQueryNumber.value;
      tempStatementOptions[StatementOptions.billQueryHours] = this.eStatementFromControls.billQueryHours.value;
      tempStatementOptions[StatementOptions.statementDueDateLeadDays] =
        this.eStatementFromControls.stmtDueDateLeadDays.value;
      tempStatementOptions[StatementOptions.statementTestMode] = this.eStatementFromControls.statementTestMode.value;
      tempStatementOptions[StatementOptions.displayAgingBoxes] = this.eStatementFromControls.displayAgingBoxes.value;
      tempStatementOptions[StatementOptions.displayInsuranceAgingBoxes] =
        this.eStatementFromControls.displayInsuranceAgingBoxes.value;
      tempStatementOptions[StatementOptions.displayDetails] = this.eStatementFromControls.displayStatementDetail.value;
      tempStatementOptions[StatementOptions.payByCheck] = this.eStatementFromControls.payOrBillByCheck.value;
      tempStatementOptions[StatementOptions.payByMoneyOrder] = this.eStatementFromControls.payOrBillByMoneyOrder.value;
      tempStatementOptions[StatementOptions.displayDetachReturnMail] =
        this.eStatementFromControls.displayDetachReturnByMail.value;
      tempStatementOptions[StatementOptions.displayMerchantName] =
        this.eStatementFromControls.displayMerchantName.value;

      this.statementService.updateStatementOptions(this.merchant.id, tempStatementOptions).subscribe(
        (statementOptions) => {
          this.merchant.statementOptions = statementOptions;
          this.fetchLogo();
        },
        (error) => {
          this.notificationService.error(error.error);
          this.actionInProgress = false;
        }
      );
    } else {
      this.eStatementForm.markAllAsTouched();
      this.actionInProgress = false;
    }
  }

  get eStatementFromControls() {
    return this.eStatementForm.controls;
  }

  validateRemitToAddress(merchant: Merchant): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (control.value) {
        if (merchant.remitToAddress) {
          return null;
        } else {
          return { noRemitAddress: true };
        }
      } else return null;
    };
  }

  receiveEditEvent(value: boolean) {
    this.editable = value;
    this.createForm();
  }
}
